import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../../../../theme';
import { Modal } from '../../../../components/ui/Modal';
import { Button } from '../../../../components/ui/Button';
import { Input } from '../../../../components/ui/Input';
import { WeaponData, deployWeaponToClient, WeaponDeploymentData } from '../service/weaponService';
import { fetchIndividualClients } from '../../../manage_clients/individual_clients/service/individualClientService';
import { fetchFactoryClients } from '../../../manage_clients/factory_clients/service/factoryClientService';
import { fetchBankClients } from '../../../manage_clients/bank_clients/service/bankClientService';
import toast from 'react-hot-toast';

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const Label = styled.label`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.light.text.primary};
`;

const Select = styled.select`
  width: 100%;
  padding: ${theme.spacing[3]};
  border: 1px solid ${theme.colors.light.border.medium};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.light.text.primary};
  background-color: ${theme.colors.white};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }
`;

const ClientList = styled.div`
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid ${theme.colors.light.border.light};
  border-radius: ${theme.borderRadius.md};
`;

const ClientItem = styled.div<{ selected?: boolean }>`
  padding: ${theme.spacing[3]};
  cursor: pointer;
  border-bottom: 1px solid ${theme.colors.light.border.light};
  background-color: ${({ selected }) =>
    selected ? theme.colors.primary[50] : theme.colors.white};
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${({ selected }) =>
      selected ? theme.colors.primary[100] : theme.colors.light.background.secondary};
  }

  &:last-child {
    border-bottom: none;
  }
`;

const ClientName = styled.div`
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.light.text.primary};
  margin-bottom: ${theme.spacing[1]};
`;

const ClientInfo = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.light.text.secondary};
`;

interface WeaponDeploymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  weapon: WeaponData | null;
  companyId: string;
  onDeploy: () => void;
}

interface ClientOption {
  id: string;
  name: string;
  type: 'Individual' | 'Factory' | 'Bank';
  path: string;
  address?: string;
  industryName?: string;
  bankName?: string;
  branchCode?: string;
}

const WeaponDeploymentModal: React.FC<WeaponDeploymentModalProps> = ({
  isOpen,
  onClose,
  weapon,
  companyId,
  onDeploy,
}) => {
  const [clientType, setClientType] = useState<'Individual' | 'Factory' | 'Bank'>('Individual');
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [selectedClient, setSelectedClient] = useState<ClientOption | null>(null);
  const [deploymentDate, setDeploymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [deploying, setDeploying] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadClients();
    }
  }, [isOpen, clientType, companyId]);

  const loadClients = async () => {
    setLoading(true);
    try {
      let clientsData: ClientOption[] = [];

      if (clientType === 'Individual') {
        const individualClients = await fetchIndividualClients(companyId);
        clientsData = individualClients.map((client) => ({
          id: client.id,
          name: client.name || '',
          type: 'Individual' as const,
          path: `Individual/${companyId}/clients/${client.id}`,
          address: client.address,
        }));
      } else if (clientType === 'Factory') {
        const factoryClients = await fetchFactoryClients(companyId);
        clientsData = factoryClients.map((client) => ({
          id: client.id,
          name: client.name || '',
          type: 'Factory' as const,
          path: `Factory/${companyId}/industries/${client.industryId}/clients/${client.id}`,
          address: client.address,
          industryName: client.industryName,
        }));
      } else if (clientType === 'Bank') {
        const bankClients = await fetchBankClients(companyId);
        clientsData = bankClients.map((client) => ({
          id: client.branchId,
          name: client.bankName || '',
          type: 'Bank' as const,
          path: `Bank/${companyId}/banks/${client.bankId}/branches/${client.branchId}`,
          address: client.address,
          bankName: client.bankName,
          branchCode: client.branchCode,
        }));
      }

      setClients(clientsData);
      setSelectedClient(null);
    } catch (error: any) {
      console.error('Error loading clients:', error);
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const handleDeploy = async () => {
    if (!weapon || !selectedClient) {
      toast.error('Please select a client');
      return;
    }

    setDeploying(true);
    try {
      const deploymentData: WeaponDeploymentData = {
        weaponId: weapon.id,
        clientId: selectedClient.id,
        clientName: selectedClient.name,
        clientType: selectedClient.type,
        clientPath: selectedClient.path,
        deploymentDate: new Date(deploymentDate),
      };

      await deployWeaponToClient(deploymentData, companyId);
      toast.success('Weapon deployed successfully');
      onDeploy();
      onClose();
    } catch (error: any) {
      console.error('Error deploying weapon:', error);
      toast.error(error.message || 'Failed to deploy weapon');
    } finally {
      setDeploying(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Deploy Weapon - ${weapon?.weaponId || ''}`}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={deploying}>
            Cancel
          </Button>
          <Button onClick={handleDeploy} loading={deploying} disabled={deploying || !selectedClient}>
            Deploy
          </Button>
        </>
      }
    >
      <ModalContent>
        <FormGroup>
          <Label>Client Type *</Label>
          <Select
            value={clientType}
            onChange={(e) => setClientType(e.target.value as 'Individual' | 'Factory' | 'Bank')}
          >
            <option value="Individual">Individual</option>
            <option value="Factory">Factory</option>
            <option value="Bank">Bank</option>
          </Select>
        </FormGroup>

        <FormGroup>
          <Label>Select Client *</Label>
          {loading ? (
            <div style={{ textAlign: 'center', padding: theme.spacing[4] }}>
              Loading clients...
            </div>
          ) : clients.length > 0 ? (
            <ClientList>
              {clients.map((client) => (
                <ClientItem
                  key={client.id}
                  selected={selectedClient?.id === client.id}
                  onClick={() => setSelectedClient(client)}
                >
                  <ClientName>{client.name}</ClientName>
                  <ClientInfo>
                    {client.type}
                    {client.address && ` • ${client.address}`}
                    {client.industryName && ` • ${client.industryName}`}
                    {client.branchCode && ` • ${client.branchCode}`}
                  </ClientInfo>
                </ClientItem>
              ))}
            </ClientList>
          ) : (
            <div style={{ textAlign: 'center', padding: theme.spacing[4], color: theme.colors.light.text.secondary }}>
              No clients found
            </div>
          )}
        </FormGroup>

        <FormGroup>
          <Label>Deployment Date *</Label>
          <Input
            type="date"
            value={deploymentDate}
            onChange={(e) => setDeploymentDate(e.target.value)}
            required
            dark
          />
        </FormGroup>
      </ModalContent>
    </Modal>
  );
};

export default WeaponDeploymentModal;

